import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {FinancialProductPreferenceRefusal} from '../../../api/types/onlineOfferTypes';

// The re-sent write replaces the refused state at once, so hold its notice separately
// for at least this long to give the customer time to read it.
const CONFLICT_NOTICE_MIN_VISIBLE_MS = 6_000;

type HeldConflictNotice = {
  refusal: FinancialProductPreferenceRefusal;
  visibleUntil: number;
};

export const useConflictNotice = (hasWriteInFlight: () => boolean) => {
  const [heldConflictNotice, setHeldConflictNotice] = useState<HeldConflictNotice | null>(null);
  const currentHeldConflictNotice = useRef<HeldConflictNotice | null>(null);
  const heldConflictNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHeldConflictNotice = () => {
    if (isNotNil(heldConflictNoticeTimer.current)) {
      clearTimeout(heldConflictNoticeTimer.current);
      heldConflictNoticeTimer.current = null;
    }

    if (isNotNil(currentHeldConflictNotice.current)) {
      currentHeldConflictNotice.current = null;
      setHeldConflictNotice(null);
    }
  };

  // The notice disappears only after both its minimum window and the rebased write have ended.
  const releaseHeldConflictNoticeWhenDue = () => {
    const held = currentHeldConflictNotice.current;

    if (!isNotNil(held) || Date.now() < held.visibleUntil || hasWriteInFlight()) {
      return;
    }

    clearHeldConflictNotice();
  };

  const holdConflictNotice = (conflict: FinancialProductPreferenceRefusal) => {
    clearHeldConflictNotice();

    const held = {refusal: conflict, visibleUntil: Date.now() + CONFLICT_NOTICE_MIN_VISIBLE_MS};
    currentHeldConflictNotice.current = held;
    setHeldConflictNotice(held);
    heldConflictNoticeTimer.current = setTimeout(
      releaseHeldConflictNoticeWhenDue,
      CONFLICT_NOTICE_MIN_VISIBLE_MS
    );
  };

  return {
    heldConflictNotice,
    clearHeldConflictNotice,
    releaseHeldConflictNoticeWhenDue,
    holdConflictNotice,
  };
};
